import { type CoursePreview, UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { ArrowUpRightIcon, AwardIcon, DownloadIcon } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";

import ConfirmationModal from "~/components/ConfirmationModal";
import {
	useAccess,
	usePassedCourses,
	useSelectedProfessions,
	useUserAction,
} from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";

interface CoursePassedCardProps {
	course: CoursePreview;
}

const CoursePassedCard = ({ course }: CoursePassedCardProps) => {
	const { passedCourses } = usePassedCourses();
	const { setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();

	const { actor } = useAuth();
	const { isMember, isTrialing } = useAccess();

	const passedCourse = useMemo(
		() =>
			passedCourses?.find(
				(passedCourse) => passedCourse.courseId === course._id,
			),
		[course._id, passedCourses],
	);

	const { selectedProfessions } = useSelectedProfessions();

	const hasSelectedProfession = !!(
		selectedProfessions?.primaryProfession ||
		selectedProfessions?.secondaryProfession
	);

	const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

	if (!passedCourse) return null;

	const hasAccess = (isMember && !isTrialing) || actor;

	const baseCertificateUrl = `${process.env.NEXT_PUBLIC_API_URL}/courses/passed/${passedCourse.id}/certificate`;

	return (
		<>
			{!hasSelectedProfession && (
				<ConfirmationModal
					open={!!selectedUrl}
					onClose={() => setSelectedUrl(null)}
					onConfirm={() => {
						window.open(selectedUrl!);
						setSelectedUrl(null);
					}}
					confirmationLabel="Proceed Anyway"
					title="No Profession Selected"
					description="We use your profession to determine applicable accreditations. Without a profession selected, your certificate will not include any accreditations."
				>
					<blockquote className="rounded-lg border border-orange-600/20 bg-orange-50 px-3 py-1.5 dark:border-orange-300/20 dark:bg-orange-300/20">
						<button
							onClick={() => {
								setSelectedUrl(null);
								setUserAction(UserAction.SELECT_A_PROFESSION);
							}}
							className="font-medium text-orange-800 hover:underline"
						>
							Click here
						</button>{" "}
						to select a profession before viewing your certificate.
					</blockquote>
				</ConfirmationModal>
			)}

			<div
				id="course-passed"
				className={clsx(
					"flex h-16 w-full scroll-m-32 justify-between gap-4 rounded-xl border px-4 py-2 shadow shadow-black/5",
					passedCourse.surveyRequired
						? "border-amber-600/20 bg-amber-50 dark:border-amber-300/20 dark:bg-amber-300/20"
						: "border-emerald-600/20 bg-emerald-50 dark:border-emerald-300/20 dark:bg-emerald-300/20",
				)}
			>
				<div className="flex items-center gap-2.5 whitespace-nowrap font-medium">
					<AwardIcon
						size={24}
						className={
							passedCourse.surveyRequired
								? "text-amber-600 dark:text-amber-200"
								: "text-emerald-700 dark:text-emerald-200"
						}
					/>{" "}
					<div className="flex flex-col">
						<span>
							{" "}
							Passed {course.credits} Credit{course.credits > 1 ? "s" : ""}
						</span>
						<span className="text-sm">
							on{" "}
							{new Date(passedCourse.createdAt).toLocaleString("en-US", {
								dateStyle: "medium",
							})}
						</span>
					</div>
				</div>

				{passedCourse.surveyRequired ? (
					<Link
						href={`/courses/${course.slug}/exams/survey`}
						className="group gap-1 text-left font-medium text-[0.95rem] hover:underline"
					>
						Take Survey <br /> For Certificate{" "}
						<ArrowUpRightIcon
							size={18}
							strokeWidth={2.25}
							className="inline-block transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
						/>
					</Link>
				) : (
					<div className="flex flex-col items-end">
						<button
							onClick={() => {
								if (hasAccess) {
									if (hasSelectedProfession) {
										window.open(baseCertificateUrl);
									} else {
										setSelectedUrl(baseCertificateUrl);
									}
								} else {
									trackCTA(
										UserAction.BECOME_A_MEMBER,
										"View Passed Course Card",
									);
									setUserAction(UserAction.BECOME_A_MEMBER);
								}
							}}
							className="group flex items-center gap-1 whitespace-nowrap font-medium text-[0.95rem] hover:underline disabled:pointer-events-none disabled:opacity-75"
						>
							View{" "}
							<ArrowUpRightIcon
								size={16}
								strokeWidth={2.25}
								className="transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
							/>
						</button>

						<button
							onClick={() => {
								if (hasAccess) {
									if (hasSelectedProfession) {
										window.open(`${baseCertificateUrl}?download=true`);
									} else {
										setSelectedUrl(`${baseCertificateUrl}?download=true`);
									}
								} else {
									trackCTA(
										UserAction.BECOME_A_MEMBER,
										"Download Passed Course Card",
									);
									setUserAction(UserAction.BECOME_A_MEMBER);
								}
							}}
							className="group flex items-center gap-1.5 whitespace-nowrap font-medium text-[0.95rem] hover:underline disabled:pointer-events-none disabled:opacity-75"
						>
							Download{" "}
							<DownloadIcon
								size={16}
								strokeWidth={2.25}
								className="transition-all group-hover:translate-y-0.5"
							/>
						</button>
					</div>
				)}
			</div>
		</>
	);
};

export default CoursePassedCard;
